import { createContext, ReactNode, useMemo, useState } from 'react';
import { useQuery } from 'react-query';

import FetchError from 'errors/FetchError';

import { retry } from 'utils/fetch';

import { fetchAreas } from './api';

type AreasContextProps = {
  isLoading: boolean;
  isFetched: boolean;
  error: FetchError | null;
  mapped?: RecordArea;
  selected?: Area;
};

export const AreasContext = createContext<AreasContextProps>({
  error: null,
  isLoading: false,
  isFetched: false,
});

function AreasContextProvider({ children }: { children: ReactNode }) {
  const [areaId, setAreaId] = useState<string | undefined>(undefined);

  const result = useQuery<Area[], FetchError>(
    'areas',
    () =>
      fetchAreas().then(({ areas, selected }) => {
        if (selected && selected !== areaId) {
          setAreaId(selected);
        }

        return areas;
      }),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry,
    },
  );

  const value = useMemo(() => {
    const mapped: RecordArea = result.data
      ? result.data.reduce(
          (acc, area) => ({
            ...acc,
            [area.id]: area,
          }),
          {},
        )
      : {};

    return {
      isLoading: result.isLoading,
      isFetched: result.isFetched,
      error: result.error,
      mapped,
      selected: areaId ? mapped[areaId] : undefined,
    };
  }, [areaId, result.data, result.error, result.isFetched, result.isLoading]);

  return <AreasContext.Provider value={value}>{children}</AreasContext.Provider>;
}

export default AreasContextProvider;
